import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, FormControl, TextField, Typography } from '@mui/material';
import type { FieldError } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

interface ChipInputProps {
  label?: string;
  inputLabel?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
}

const ChipInput: FC<ChipInputProps> =
  ({
     label,
     inputLabel,
     name,
     required,
     disabled,
   }) => {
    const { control, setValue, formState, trigger, watch } = useFormContext();
    const formError = formState.errors[name];
    const formValue = watch(name) as string[];

    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      setInputValue('');
    }, [name]);

    const errorMessage = useMemo(() => {
      if (Array.isArray(formError)) {
        return (formError as FieldError[]).filter((err) => !!err.message)[0]
          .message;
      }

      return formError?.message;
    }, [formError]);

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value?.trim());
      },
      [setInputValue],
    );

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const value = (event.target as HTMLInputElement).value;
        if (value.length) {
          setValue(name, [...(formValue ?? []), value], { shouldDirty: true });
          setInputValue('');
          void trigger(name);
        }
      } else if (event.key === 'Backspace' && !inputValue.length) {
        const newValues = [...(formValue ?? [])];
        newValues.pop();
        setValue(name, newValues.length ? newValues : undefined, {
          shouldDirty: true,
        });
        void trigger(name);
      }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value;
      if (value.length) {
        setValue(name, [...(formValue ?? []), value], { shouldDirty: true });
        setInputValue('');
        void trigger(name);
      }
    };

    const handleDelete = useCallback(
      (index: number) => {
        const newValues = [...formValue];
        newValues.splice(index, 1);
        setValue(name, newValues.length ? newValues : [], { shouldDirty: true });
        void trigger(name);
      },
      [setValue, trigger, name, formValue],
    );

    const handlePaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = event.clipboardData.getData('text');
        const parsedValue = pastedData.split('\n').map((item) => item.trim()).filter(Boolean);
        setValue(name, [...(formValue ?? []), ...parsedValue], { shouldDirty: true });
        void trigger(name);
        event.preventDefault();
      },
      [formValue, name, setValue, trigger],
    );

    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          return (
            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
              {label && !inputLabel && (
                <Typography variant="subtitle2" mb={1}>
                  {label}
                  {required && '*'}
                </Typography>
              )}
              <FormControl sx={{ flex: 1 }}>
                <TextField
                  {...field}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleInputChange(e);
                  }}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    setIsFocused(false);
                    handleOnBlur(e);
                  }}
                  onFocus={() => {
                    setIsFocused(true);
                  }}
                  onPaste={handlePaste}
                  value={inputValue}
                  onKeyDown={handleKeyPress}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      maxHeight: 'fit-content',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'start',
                      gap: 1,
                      py: 1,
                      px: 1.25,
                      '& .MuiOutlinedInput-notchedOutline legend': {
                        transition: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-root input': {
                      display: 'inline-flex',
                      width: 'auto',
                      minWidth: 120,
                      py: 0,
                      height: 24,
                      borderRadius: 0,
                    },
                  }}
                  InputLabelProps={{
                    shrink: formValue?.length > 0 || !!inputValue || isFocused,
                  }}
                  InputProps={{
                    startAdornment: formValue
                      ? (formValue as unknown as string[])?.map(
                        (value, index) => (
                          <Chip
                            key={`${name}_chip_${index}`}
                            label={value}
                            onDelete={
                              disabled
                                ? undefined
                                : () => {
                                  handleDelete(index);
                                }
                            }
                            sx={{ '&': { m: 0, display: 'inline-flex' } }}
                          />
                        ),
                      )
                      : null,
                    disabled: disabled,
                  }}
                  label={inputLabel}
                  error={!!formError}
                />
                {errorMessage && (
                  <Typography
                    variant="subtitle1"
                    fontSize={12}
                    sx={{ mt: '3px' }}
                    color="error"
                  >
                    {errorMessage as string}
                  </Typography>
                )}
              </FormControl>
            </Box>
          );
        }}
      />
    );
  };

export default ChipInput;
